import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { Business } from '../types';

export interface RoleProtectionResult {
  isProtected: boolean;
  reason?: string;
  canEdit: boolean;
  canDeactivate: boolean;
  canDelete: boolean;
}

export interface RoleHierarchy {
  id: string;
  name: string;
  level: number;
}

export interface UserRoleInfo {
  id: string;
  name: string;
  description: string;
  businessId: string;
  isActive: boolean;
}

export const ROLE_HIERARCHY_LEVELS = {
  'Business Owner': 1,
  'Manager': 2,
  'Professional': 3,
  'Assistant': 4
} as const;

export type RoleName = keyof typeof ROLE_HIERARCHY_LEVELS;

export const useRoleProtection = () => {
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  const [roleHierarchy, setRoleHierarchy] = useState<RoleHierarchy[]>([]);
  const [userRole, setUserRole] = useState<UserRoleInfo | null>(null);
  const [loading, setLoading] = useState(false);

  /**
   * Get role protection information for a user in a business
   */
  const checkRoleProtection = useCallback((
    userId: string, 
    businessId: string, 
    targetUserId?: string
  ): RoleProtectionResult => {
    if (!userRole) {
      return {
        isProtected: false,
        canEdit: true,
        canDeactivate: true,
        canDelete: true
      };
    }

    // If checking protection for the same user (self-protection)
    if (targetUserId && userId === targetUserId) {
      return getSelfProtection(userRole.name);
    }

    // Default: no protection for other users (Business Owner can manage other personnel)
    return {
      isProtected: false,
      canEdit: true,
      canDeactivate: true,
      canDelete: true
    };
  }, [userRole]);

  /**
   * Get protection rules for business owner role
   */
  const getBusinessOwnerProtection = (roleName: string): RoleProtectionResult => {
    return {
      isProtected: true,
      reason: `${roleName} role is protected from modification`,
      canEdit: false,
      canDeactivate: false,
      canDelete: false
    };
  };

  /**
   * Get protection rules for self-modification
   */
  const getSelfProtection = (roleName: string): RoleProtectionResult => {
    return {
      isProtected: true,
      reason: `Cannot modify your own ${roleName} role`,
      canEdit: false,
      canDeactivate: false,
      canDelete: false
    };
  };

  /**
   * Check if a user is a business owner in a specific business
   */
  const isBusinessOwner = useCallback((userId: string, businessId: string): boolean => {
    if (!userRole) return false;
    return userRole.name === 'Business Owner' && userRole.businessId === businessId;
  }, [userRole]);

  /**
   * Get role hierarchy for a business
   */
  const getRoleHierarchy = useCallback((businessId: string): RoleHierarchy[] => {
    return roleHierarchy.filter(role => role.id === businessId);
  }, [roleHierarchy]);

  /**
   * Validate role dependencies for a user
   */
  const validateRoleDependencies = useCallback((userId: string, businessId: string): boolean => {
    // For now, we'll assume users with roles are valid
    // In a real implementation, this would check against the backend
    return userRole !== null && userRole.isActive;
  }, [userRole]);

  /**
   * Get user role in a specific business
   */
  const getUserRoleInBusiness = useCallback((userId: string, businessId: string): UserRoleInfo | null => {
    if (userRole && userRole.businessId === businessId) {
      return userRole;
    }
    return null;
  }, [userRole]);

  /**
   * Check if current user can perform an action on a target user
   */
  const canPerformAction = useCallback((
    action: 'edit' | 'deactivate' | 'delete',
    targetUserId?: string
  ): boolean => {
    if (!user || !currentBusiness) return false;

    const protection = checkRoleProtection(user.id, currentBusiness.id, targetUserId);
    
    switch (action) {
      case 'edit':
        return protection.canEdit;
      case 'deactivate':
        return protection.canDeactivate;
      case 'delete':
        return protection.canDelete;
      default:
        return false;
    }
  }, [user, currentBusiness, checkRoleProtection]);

  /**
   * Get role protection status for display
   */
  const getRoleProtectionStatus = useCallback((targetUserId?: string) => {
    if (!user || !currentBusiness) {
      return {
        isProtected: false,
        reason: 'Not authenticated',
        canEdit: false,
        canDeactivate: false,
        canDelete: false
      };
    }

    return checkRoleProtection(user.id, currentBusiness.id, targetUserId);
  }, [user, currentBusiness, checkRoleProtection]);

  /**
   * Load user role information
   */
  const loadUserRole = useCallback(async () => {
    if (!user || !currentBusiness) {
      setUserRole(null);
      return;
    }

    setLoading(true);
    try {
      // Find the user's role in the current business
      const userRoleInBusiness = user.roles?.find(role => 
        role.businessId === currentBusiness.id
      );
      
      // Check if the role is active (both user role and business role should be active)
      const isRoleActive = userRoleInBusiness?.isActive && userRoleInBusiness?.businessRoleActive !== false;
      
      // If role is not active, treat as customer
      if (!isRoleActive) {
        const customerRole: UserRoleInfo = {
          id: 'customer-role',
          name: 'Customer',
          description: 'Customer role (no business permissions)',
          businessId: currentBusiness.id,
          isActive: false
        };
        setUserRole(customerRole);
        return;
      }
      
      // Map role values to display names based on permissions
      const getRoleDisplayName = (role: any): string => {
        if (role.permissions?.some((p: string) => ['manage_settings', 'manage_roles', 'manage_professionals'].includes(p))) {
          return 'Business Owner';
        } else if (role.permissions?.some((p: string) => ['manage_appointments', 'manage_professionals'].includes(p))) {
          return 'Manager';
        } else if (role.permissions?.some((p: string) => ['manage_availability', 'view_schedule'].includes(p))) {
          return 'Professional';
        } else if (role.permissions?.some((p: string) => ['view_appointments', 'view_services'].includes(p))) {
          return 'Assistant';
        } else if (!role.permissions || role.permissions.length === 0) {
          return 'Customer';
        } else {
          return role.role || 'Professional';
        }
      };
      
      const userRoleName = getRoleDisplayName(userRoleInBusiness);
      const mockUserRole: UserRoleInfo = {
        id: userRoleInBusiness?.id || 'mock-role-id',
        name: userRoleName,
        description: 'User role in business',
        businessId: currentBusiness?.id || '',
        isActive: isRoleActive
      };

      setUserRole(mockUserRole);
    } catch (error) {
      console.error('Error loading user role:', error);
      setUserRole(null);
    } finally {
      setLoading(false);
    }
  }, [user, currentBusiness]);

  /**
   * Load role hierarchy
   */
  const loadRoleHierarchy = useCallback(async () => {
    if (!currentBusiness) {
      setRoleHierarchy([]);
      return;
    }

    setLoading(true);
    try {
      // In a real implementation, this would fetch from the backend
      // For now, we'll use the predefined hierarchy
      const mockHierarchy: RoleHierarchy[] = [
        { id: currentBusiness.id, name: 'Business Owner', level: 1 },
        { id: currentBusiness.id, name: 'Manager', level: 2 },
        { id: currentBusiness.id, name: 'Professional', level: 3 },
        { id: currentBusiness.id, name: 'Assistant', level: 4 }
      ];

      setRoleHierarchy(mockHierarchy);
    } catch (error) {
      console.error('Error loading role hierarchy:', error);
      setRoleHierarchy([]);
    } finally {
      setLoading(false);
    }
  }, [currentBusiness]);

  // Load data when user or business changes
  useEffect(() => {
    loadUserRole();
    loadRoleHierarchy();
  }, [loadUserRole, loadRoleHierarchy]);

  return {
    // State
    roleHierarchy,
    userRole,
    loading,

    // Methods
    checkRoleProtection,
    isBusinessOwner,
    getRoleHierarchy,
    validateRoleDependencies,
    getUserRoleInBusiness,
    canPerformAction,
    getRoleProtectionStatus,

    // Utilities
    ROLE_HIERARCHY_LEVELS,
    loadUserRole,
    loadRoleHierarchy
  };
};

export default useRoleProtection; 